/**
 * The Current Balance card's own formatting logic: which of the four balance states to render,
 * and whether the balance is past due. Locale-aware currency/date display formatting lives in the
 * shared `useCurrency`/`useFormatDate` hooks; this file only holds the branching that's specific
 * to this card.
 */

import { isBefore, parseISO } from 'date-fns';

/**
 * `unavailable` covers a null `amount` — distinct from `paid` (`amount === 0`) because a member
 * whose balance we don't know is not the same as a member who owes nothing. Coercing null to 0
 * would render "You're all paid up" for a balance we can't actually vouch for.
 */
export type BalanceState = 'unavailable' | 'due' | 'paid' | 'credit';

export function deriveBalanceState(amount: number | null): BalanceState {
  if (amount === null) return 'unavailable';
  if (amount < 0) return 'credit';
  if (amount === 0) return 'paid';
  return 'due';
}

/**
 * True when `dueDate` is on or before `now` — matches legacy's past-due determination without
 * legacy's re-derivation of the day count from a formatted string (the backend's own
 * `billingDaysPastDue` is used for that instead). A due date of today is `overdue`: `parseISO`
 * lands on local midnight, which is before any later time today, so "Due Today" stays reachable.
 */
export function isOverdue(dueDate: string, now: Date = new Date()): boolean {
  return isBefore(parseISO(dueDate), now);
}
